import styled from '@emotion/styled';
import { Box, Chip, Container, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import Copyright from './Copyright';

const Page = styled(Box)`
  position: relative;
  min-height: 100%;
  overflow: hidden;
  background: ${({ theme }) => theme.customTokens.surfaces.pageBackdrop},
    linear-gradient(
      135deg,
      ${({ theme }) => theme.palette.primary.dark},
      ${({ theme }) => theme.palette.primary.main} 58%,
      ${({ theme }) => theme.palette.secondary.dark}
    );

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image: linear-gradient(
        ${({ theme }) => alpha(theme.palette.common.white, 0.05)} 1px,
        transparent 1px
      ),
      linear-gradient(
        90deg,
        ${({ theme }) => alpha(theme.palette.common.white, 0.05)} 1px,
        transparent 1px
      );
    background-size: 56px 56px;
    mask-image: linear-gradient(
      90deg,
      ${({ theme }) => alpha(theme.palette.common.black, 0.75)},
      transparent 72%
    );
  }
`;

const ContainerMain = styled(Container)`
  position: relative;
  z-index: 1;
  min-height: 100vh;
  display: flex;
  gap: ${({ theme }) => theme.spacing(5)};
  align-items: center;
  justify-content: center;
  padding-top: ${({ theme }) => theme.spacing(4)};
  padding-bottom: ${({ theme }) => theme.spacing(4)};

  ${({ theme }) => theme.breakpoints.down('md')} {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(3)};
    align-items: stretch;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding-top: ${({ theme }) => theme.spacing(2)};
    padding-bottom: ${({ theme }) => theme.spacing(8)};
  }
`;

const BrandPanel = styled(Paper)`
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 520px;
  padding: ${({ theme }) => theme.spacing(4)};
  color: ${({ theme }) => theme.palette.primary.contrastText};
  border: 1px solid ${({ theme }) => alpha(theme.palette.common.white, 0.16)};
  background: ${({ theme }) => alpha(theme.palette.common.white, 0.1)};
  backdrop-filter: blur(18px);
  box-shadow: ${({ theme }) => `0 30px 80px ${alpha(theme.palette.common.black, 0.24)}`};

  &::after {
    content: '';
    position: absolute;
    right: -120px;
    bottom: -120px;
    width: 260px;
    height: 260px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      ${({ theme }) => alpha(theme.palette.secondary.main, 0.45)},
      transparent 68%
    );
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    max-width: none;
    padding: ${({ theme }) => theme.spacing(3)};
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding: ${({ theme }) => theme.spacing(2.5)};
  }
`;

const LoginPanel = styled(Box)`
  width: 100%;
  max-width: 430px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    max-width: none;
  }
`;

const CopyrightWrapper = styled(Box)`
  position: absolute;
  left: 0;
  right: 0;
  bottom: ${({ theme }) => theme.spacing(2)};
  z-index: 1;

  a,
  span,
  p {
    color: ${({ theme }) => alpha(theme.palette.primary.contrastText, 0.72)};
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    bottom: ${({ theme }) => theme.spacing(1)};
  }
`;

const PublicLayout = () => {
  const { t } = useTranslation();

  return (
    <Page component="main">
      <ContainerMain maxWidth="lg">
        <BrandPanel elevation={0}>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Chip
              label={t('publicLayout.badge')}
              color="secondary"
              size="small"
              sx={theme => ({ mb: 2, fontWeight: theme.typography.overline.fontWeight })}
            />
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t('shared.title')}
            </Typography>
            <Typography
              variant="h6"
              sx={theme => ({ color: alpha(theme.palette.primary.contrastText, 0.82), mb: 3 })}
            >
              {t('publicLayout.subtitle')}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <Chip
                label={t('publicLayout.highlights.executiveDashboard')}
                variant="outlined"
                sx={theme => ({ color: theme.palette.primary.contrastText })}
              />
              <Chip
                label={t('publicLayout.highlights.centralizedReports')}
                variant="outlined"
                sx={theme => ({ color: theme.palette.primary.contrastText })}
              />
              <Chip
                label={t('publicLayout.highlights.secureAccess')}
                variant="outlined"
                sx={theme => ({ color: theme.palette.primary.contrastText })}
              />
            </Stack>
          </Box>
        </BrandPanel>
        <LoginPanel>
          <Outlet />
        </LoginPanel>
      </ContainerMain>
      <CopyrightWrapper>
        <Copyright />
      </CopyrightWrapper>
    </Page>
  );
};

export default PublicLayout;
